// PrivacyPolicyPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const PrivacyPolicyPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Legal & Company', path: '/legal' },
    { name: 'Privacy Policy', path: '/legal/privacy-policy' },
  ];

  const sections = [
    {
      title: '1. Information We Collect',
      content: 'We collect personal information you provide directly to us, such as your name, email address, contact details, and payment information when you register an account, use our services, or communicate with us.',
      subsections: [
        'Personal Identifiable Information (PII)',
        'Usage Data (e.g., IP address, browser type)',
        'Transaction Data',
      ],
    },
    {
      title: '2. How We Use Your Information',
      content: 'We use the information we collect to provide, maintain, and improve our services, process transactions, communicate with you, and ensure the security of our platform.',
      subsections: [
        'Service provision and personalization.',
        'Communication and customer support.',
        'Security and fraud prevention.',
      ],
    },
    {
      title: '3. Sharing of Information',
      content: 'We do not share your personal information with third parties except as necessary to provide our services, comply with legal obligations, or with your explicit consent.',
      subsections: [
        'With service providers (e.g., payment processors, logistics partners).',
        'For legal reasons (e.g., court orders, regulatory compliance).',
        'In case of business transfers (e.g., merger or acquisition).',
      ],
    },
    {
      title: '4. Your Rights and Choices',
      content: 'You have certain rights regarding your personal information, including the right to access, correct, delete, or restrict its processing. You can also object to processing or withdraw consent.',
      subsections: [
        'Access and rectification.',
        'Erasure ("right to be forgotten").',
        'Data portability.',
      ],
    },
    {
      title: '5. Data Security',
      content: 'We implement technical and organizational measures designed to protect your personal information from unauthorized access, use, or disclosure. However, no internet transmission is entirely secure.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Privacy Policy
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our platform.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {sections.map((section, index) => (
            <Box
              key={index}
              sx={{
                mb: 4,
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                background: 'background.paper',
                border: '1px solid',
                borderColor: 'grey.100',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
              }}
            >
              <Typography variant="h5" fontWeight={600} sx={{ mb: 1.5, color: 'primary.dark' }}>
                {section.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: section.subsections ? 2 : 0 }}>
                {section.content}
              </Typography>
              {section.subsections && (
                <List dense disablePadding>
                  {section.subsections.map((sub, subIndex) => (
                    <ListItem key={subIndex} disablePadding sx={{ py: 0.2 }}>
                      <ListItemText primary={`- ${sub}`} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
          Last updated: July 29, 2024
        </Typography>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
